<template>
  <div>
    <div id="my-container">
      <b-navbar id="header" type="light" variant="light" sticky>
        <b-navbar-nav>
          <h4 class="mb-0">{{title}}</h4>
        </b-navbar-nav>

        <b-navbar-nav class="ml-auto">
          <b-button v-if="canEditTasks()" size="sm" variant="success" :disabled="isBusy" @click="onCreate()">
            <b-spinner v-if="isBusy" small/>
            <font-awesome-icon icon="plus" class="mr-1"/>
            Новая
          </b-button>
        </b-navbar-nav>
      </b-navbar>

      <b-navbar variant="light">
          <b-navbar-nav >
            <filters-drawer
              :query.sync="filters.query"
              :is-busy="isBusy"
              :filter-tags="activeFilterTags"
              @remove-tag="removeFilterTag"
              @submit="onSearch"
              @clear="onClearFilters"
            >
              <b-col cols="12">
                <label class="small text-muted">Период</label>
                <b-row class="align-items-center">
                  <b-col>
                    <b-form-input
                      id="input-start-date"
                      size="sm"
                      type="date"
                      v-model="filters.start_date"
                    />
                  </b-col>
                  <b-col cols="auto" class="px-2">
                    <span>&mdash;</span>
                  </b-col>
                  <b-col>
                    <b-form-input
                      id="input-end-date"
                      size="sm"
                      type="date"
                      v-model="filters.end_date"
                    />
                  </b-col>
                </b-row>
              </b-col>
              <b-col cols="12" class="mt-1">
                <label class="small text-muted">Объекты</label>
                <location-multi-select
                  id="location-multi-select"
                  ref="location_multi_select"
                  v-on:onSelect="onSelectLocation"
                  :start-date="filters.start_date"
                  :end-date="filters.end_date"
                  :show-date-button="true"
                />
              </b-col>
              <b-col v-if="visibleAdmins()" cols="12" class="mt-1">
                <label class="small text-muted">Сотрудники</label>
                <user-multi-select
                  id="user-multi-select"
                  ref="user_multi_select"
                  v-on:onSelect="onSelectUser"
                />
              </b-col>
              <b-col md="6" cols="12" class="mt-1">
                <label class="small text-muted">Статус заявки</label>
                <b-form-select
                  id="input-statuses"
                  size="sm"
                  v-model="filters.status_id"
                >
                  <option :value="0">Все статусы</option>
                  <option v-for="option in activeStatuses" :key="option.id" :value="option.id">
                    {{ option.name }}
                  </option>
                </b-form-select>
              </b-col>
              <b-col md="6" cols="12" class="mt-1">
                <label class="small text-muted">Статус оплаты</label>
                <b-form-select
                  id="input-paid-workers-statuses"
                  size="sm"
                  v-model="filters.paid_workers"
                >
                  <option :value="0">Все статусы оплаты</option>
                  <option v-for="option in activePaidWorkersStatuses" :key="option.id" :value="option.id">
                    {{ option.name }}
                  </option>
                </b-form-select>
              </b-col>
            </filters-drawer>
          </b-navbar-nav>
          <b-navbar-nav class="ml-auto">
            <b-input-group size="sm">
                <b-dropdown id="dropdown-reports" right size="sm" class="mr-1" text="Отчеты" variant="info">
                  <template #button-content>
                    <font-awesome-icon icon="table"/>
                    Отчеты
                  </template>
                  <b-dropdown-item-button :disabled="isBusyExport" @click="exportTimesheetFile()">
                    <b-spinner v-if="isBusyExport" small/>
                    Табель
                  </b-dropdown-item-button>
                  <b-dropdown-item-button @click="exportWorkTimesheetFile()">
                    Сверка
                  </b-dropdown-item-button>
                </b-dropdown>

                <ExportSidebar
                  sidebar-id="sidebar-tasks-export"
                  storage-key="sidebar_tasks_export"
                  :columns="visibleExportColumns"
                  :data-provider="provideExportData"
                  :data-mapper="mapTaskForExport"
                  file-name="Tasks.xlsx"
                  sheet-name="Tasks"
                  :is-busy="isBusy"
                  :items-count="totalRows"
                >
                  <template #extra-buttons>
                    <b-button size="sm" variant="warning" @click="toggleTW()" class="ml-1">
                      <font-awesome-icon icon="info" class="mr-1"/>
                      {{ isIncludeTW ? 'Скрыть детализацию' : 'Детализация' }}
                    </b-button>
                  </template>
                </ExportSidebar>

                <b-button id="table-import" size="sm" class="mr-2" v-if="visibleAdmins()" :disabled="isBusyExport" variant="info" @click="readExcel()">
                  <font-awesome-icon icon="file-download"/>
                </b-button>
                <b-tooltip target="table-import" triggers="hover" variant="info" noninteractive>
                  Импорт
                </b-tooltip>

                <b-button-group class="mr-1">
                  <b-button v-b-toggle.table-view variant="outline-primary" size="sm" :pressed="currentView === 'table'" @click="currentView = 'table'">📋 Таблица</b-button>
                  <b-button v-b-toggle.calendar-view variant="outline-primary" size="sm" :pressed="currentView === 'calendar'" @click="currentView = 'calendar'">📅 Календарь</b-button>
                </b-button-group>

                <b-button id="table-settings" size="sm" variant="warning" class="mr-2" v-b-toggle.sidebar-fields v-if="currentView === 'table'">
                  <font-awesome-icon icon="cog"/>
                </b-button>
                <b-tooltip target="table-settings" triggers="hover" variant="warning" noninteractive>
                  Колонки
                </b-tooltip>
              </b-input-group>
          </b-navbar-nav>
        </b-navbar>

      <b-card v-if="currentView === 'calendar'">
        <FullCalendar :options="calendarOptions" />
      </b-card>

      <b-table
            v-else
            id="tasks"
            ref="tasks"
            class="mb-0 text-small"
            :style="tableStyle"
            select-mode="multi"
            selectedVariant="success"
            striped
            small
            responsive
            show-empty
            :empty-text="$t('message.common.emptyTable')"
            :busy="isBusy"
            :items="items"
            :fields="renderedFields"
            no-provider-filtering
            no-provider-paging
            no-provider-sorting
            no-local-sorting
            @sort-changed="sortChanged"
            @row-dblclicked="onRowClicked"
            @row-clicked="rowClicked"
            :tbody-tr-class="tbodyRowClass"
            sticky-header
            head-variant="light"
          >
            <template #table-busy>
              <div class="text-center text-success my-2">
                <b-spinner class="align-middle"/>
                <strong>Загрузка</strong>
              </div>
            </template>

            <template #head(selected)="row">
              <b-form-checkbox
                class="mx-1"
                v-model="allSelected"
                :indeterminate="indeterminate"
                :disabled = "allSelectDisabled"
                size="md"
                @change="selectAllRows"
              />
            </template>

            <template #cell(selected)="row">
              <template v-if="row.item.selected">
                <span aria-hidden="true">&check;</span>
                <span class="sr-only">Выбрана</span>
              </template>
              <template v-else>
                <span >&nbsp;</span>
                <span class="sr-only">Не выбрана</span>
              </template>
            </template>

            <template #cell(action)="row">
              <b-dropdown size="sm" no-caret>
                <template #button-content>
                  <font-awesome-icon :icon="['fas', 'ellipsis-v']"/>
                </template>
                <b-dropdown-item-button variant="success" @click.stop="onRead(row.item)" target="_blank">
                  <font-awesome-icon icon="eye"/>
                  Смотреть
                </b-dropdown-item-button>
                <b-dropdown-item-button v-if="isVisibleReadOnlyForRow(row.item)" variant="primary" @click="onEdit(row.item)">
                  <font-awesome-icon icon="edit"/>
                  Редактировать
                </b-dropdown-item-button>
                <b-dropdown-item-button variant="warning" @click="onCopy(row.item)">
                  <font-awesome-icon icon="copy"/>
                  Копировать
                </b-dropdown-item-button>
                <b-dropdown-item-button v-if="row.item.invoice_numb" variant="info" @click="onInvoiceFile(row.item)">
                  <font-awesome-icon icon="copy"/>
                  Скачать счёт
                </b-dropdown-item-button>
                <b-dropdown-item-button v-if="isVisibleReadOnlyForRow(row.item)" variant="danger" @click="onRemove(row)">
                  <font-awesome-icon :icon="['fas', 'trash']"/>
                  Удалить
                </b-dropdown-item-button>
              </b-dropdown>
            </template>

            <template #cell(location_name)="row">
              {{row.item.location_name}}
              <p class="m-0 p-0 text-secondary text-small">{{row.item.customer_name}}</p>
            </template>

            <template #cell(payment_type_int)="row">
              <font-awesome-icon :set="pt = paymentTypeIcon(row.item.payment_type_int)" :icon="['fas', pt.icon]" :title="pt.title"/>
            </template>

            <template #cell(start_date)="row">
              {{formatTableDateTime(row.item.start_date)}}
            </template>

            <template #cell(task_workers)="row">
              {{len(row.item.task_workers)}}
            </template>

            <template #cell(paid_workers)="row">
              <font-awesome-icon :icon="['fas', paidWorkers(row).icon]" :style=paidWorkers(row).style />
            </template>

            <template #cell(status_id)="row">
              <div class="d-flex justify-content-between align-items-center">
            <span style="font-size: medium">
              <b-badge pill :variant="getStatusStyle(row.item.status_id)">
                {{getStatusName(row.item.status_id)}}
              </b-badge>
            </span>
                <span>
              <font-awesome-icon
                :icon="['fas', paidWorkers(row).icon]"
                :style=paidWorkers(row).style
                v-b-tooltip.hover.top title="Оплата исполнителям"
              />
              <font-awesome-icon
                :icon="['fas', readOnly(row).icon]"
                :style=readOnly(row).style
                v-b-tooltip.hover.top title="Редактирование заявки"
              />
            </span>
              </div>
            </template>

            <template #cell(invoice_numb)="row">
              <b-button v-if="visibleAdmins() && row.item.invoice_numb" size="sm" variant="link" @click.stop="showInvoice(row.item)">
                {{row.item.invoice_numb}}
              </b-button>
              <div v-else>
                {{row.item.invoice_numb}}
              </div>
            </template>

            <template #cell(executor_name)="row">
              {{row.item.executor_name}}
              <p class="m-0 p-0 text-small">{{row.item.total_manager}} ₽</p>
            </template>

            <template #cell(location_rate)="row">
              {{row.item.payment_first}} ₽
              <p class="m-0 p-0 text-small">{{ getUnit(row.item.unit_code) }}</p>
            </template>

            <template #cell(read_only)="row">
              <font-awesome-icon :icon="['fas', readOnly(row).icon]" :style=readOnly(row).style />
            </template>

            <template #cell(workers_count)="row">
              {{ getWorkersCountText(row.item) }}
            </template>

          </b-table>

      <pagination-footer
        :total-rows="totalRows"
        :per-page.sync="perPage"
        @page-click="onPagination"
        @per-page-change="onPerPageChange"
      >
        <template #footer-update>
          <b-row v-if="selected.length > 0" align-h="between" align-v="center">
            <b-col cols="auto" class="mr-auto text-small">
              Выбрано: <b>{{selected.length}}</b>
              | С заказчика: <b>{{totalSelectedPrice}}</b>
              | Кол-во исп: <b>{{totalSelectedWorkers}}</b>
              | Кол-во ед: <b>{{totalSelectedHours}}</b>
              | Исполнителям: <b>{{totalSelectedWorkerPrice}}</b>
            </b-col>
            <b-col cols="auto">
              <b-form inline>
                <task-status-select
                  @onSelect="onSelectTaskStatus($event)"
                  :is-creatable=false
                  class="mr-2"
                  style="width: 200px"
                />

                <b-button v-if="visibleAdmins()" size="sm" variant="warning" @click="onPayWorkers()" class="mr-2">
                  <font-awesome-icon icon="receipt" class="mr-1"/>
                  Оплатить исполнителям
                </b-button>

                <b-button v-if="visibleAdmins()" size="sm" variant="info" @click="onInvoice()">
                  <font-awesome-icon icon="file-invoice-dollar" class="mr-1"/>
                  Выставить счёт
                </b-button>
              </b-form>


            </b-col>
          </b-row>
          <hr v-if="selected.length > 0"/>
        </template>
      </pagination-footer>
    </div>

    <tasks-import ref="importTasks"/>

    <dynamic-tables
      storage-key="tasks_columns"
      sidebar-fields="sidebar-fields"
      :fields-model.sync="fields"
      :serviceStartField="serviceStartField"
      :serviceEndField="serviceEndField"
      fieldKey="id"
    />

    <b-modal
      id="modal-select-pay-organization"
      ref="modalSelectPayOrganization"
      title="Организация"
      @ok="onPay()"
      ok-title="Оплатить"
      button-size="sm"
      :ok-disabled="this.currentPayOrganization == null"
      :cancel-title="$t('message.actions.cancel')"
    >
      <organization-select
        id="input-org-select"
        ref="organization_select"
        v-bind:initial-id="0"
        v-on:onSelect="onSelectOrg"
      />
    </b-modal>
  </div>
</template>

<script>
import tasks from "../../network/tasks";
import users from "../../network/users";
import invoices from "../../network/invoices";
import balansPro from "../../network/balansPro";
import dateFormatter from '../../utilites/dateFormatter';
import helpers from "../../utilites/helpers";
import permissions, {PERM} from "../../utilites/permissions";
import FiltersDrawer from "../../utilites/commonUi/FiltersDrawer.vue";
import ExportSidebar from "../../utilites/commonUi/ExportSidebar.vue";
import unitsStore from "../../utilites/unitStore";
import DynamicTables from "../../utilites/commonUi/DynamicTables.vue";
import PaginationFooter from "../../utilites/commonUi/PaginationFooter.vue";
import pageSearchService from "../../utilites/pageSearchService";
import TasksImport from "./TasksImport";
import LocationMultiSelect from "../Locations/LocationMultiSelect";
import UserMultiSelect from "../Users/UserMultiSelect";
import {parse} from "content-disposition-attachment";
import OrganizationSelect from "../Organizations/OrganizationSelect";
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import bootstrapPlugin from '@fullcalendar/bootstrap'
import multiMonthPlugin from '@fullcalendar/multimonth'
import catalogs from "../../network/catalogs";
import TaskStatusSelect from "../Catalogs/TaskStatuses/TaskStatusSelect";

export default {
  name: 'Tasks',
  components: { PaginationFooter, ExportSidebar, FiltersDrawer, DynamicTables, TasksImport, LocationMultiSelect,
    UserMultiSelect, OrganizationSelect, FullCalendar, TaskStatusSelect },
  mixins: [
    tasks, users, invoices, balansPro,
    dateFormatter, helpers, unitsStore, permissions, pageSearchService, catalogs
  ],
  data() {
    return {
      currentView: 'table', // по умолчанию открываем таблицу
      title: 'Заявки',
      isBusy: false,
      isBusyExport: false,
      items: [],
      serviceStartField: {
        key: 'selected',
        label: '',
        class:'tasksSelect'
      },
      fields: [
        {
          key: 'id',
          label: 'id',
          sortable: true,
          class:'tasksId',
          visible: true
        },
        {
          key: 'location_name',
          label: this.$t('message.tasks.table.location_name'),
          sortable: true,
          class:'tasksLocation',
          visible: true
        },
        {
          key: 'start_date',
          label: 'Дата',
          sortable: true,
          class:'tasksDate',
          visible: true
        },
        {
          key: 'workers_count',
          label: this.$t('message.tasks.table.workers_count'),
          sortable: true,
          class:'tasksWorkers',
          visible: true
        },
        {
          key: 'total_hours',
          label: 'Кол-во ед.',
          sortable: true,
          class:'tasksTotalHours',
          visible: true
        },
        {
          key: 'total_worker_price_with_comiss',
          label: 'Исполнителям ₽',
          sortable: true,
          class:'tasksTotalWorkerPrice',
          visible: true
        },
        {
          key: 'total_price',
          label: 'C заказчика ₽',
          sortable: true,
          class:'tasksTotalPrice',
          visible: true
        },
        {
          key: 'invoice_numb',
          label: 'Счёт',
          sortable: true,
          class:'tasksInvoice',
          visible: true
        },
        {
          key: 'status_id',
          label: 'Статус',
          sortable: true,
          class:'tasksStatus',
          visible: true
        },
        {
          key: 'executor_name',
          label: 'Менеджер',
          sortable: true,
          class:'tasksExecutor',
          visible: true
        },
        {
          key: 'location_rate',
          label: 'Ставка объекта',
          sortable: true,
          class:'tasksLocationRate',
          visible: true
        },
      ],
      serviceEndField: {
        key: 'action',
        label: this.$t('message.tasks.table.actions'),
        class:'tasksAction'
      },
      sortBy: 'id',
      sortDesc: true,
      filters: this.getDefaultFilters(),
      deleteRow: {
        id: null,
        start_address: ''
      },
      executors: [],
      selected: [],
      allSelected: false,
      indeterminate: false,
      allSelectDisabled: true,
      tableStyle: {
        maxHeight: 'calc(100vh - 209px) !important',
        contentVisibility: 'auto'
      },
      statuses: [],
      paidStatuses: [
        {id: 1, name: 'Не оплачено'},
        {id: 2, name: 'Оплачено'},
      ],
      tasksExportColumns: {
        "Номер заявки": true,
        "Заказчик": true,
        "Адрес заявки": true,
        "Объект": true,
        "Дата начала": true,
        "Дата окончания": true,
        "Итого исполнителям": true,
        "Итого с заказчика": true,
        "Ставка вида работ": true,
        "Менеджер": true,
        "Автор": true,
        "Дата создания": true,
        "Количество исполнителей": true,
        "Добавленные расходы": true,
        "Заявленные расходы": true,
        "Примечание": true,
        "Статус": true,
        "Исп. Имя": false,
        "Исп. Телефон": false,
        "Исп. ИНН": false,
        "Исп. Ставка": false,
        "Исп. Дата начала": false,
        "Исп. Дата окончания": false,
        "Исп. Обед": false,
        "Исп. Количество": false,
        "Исп. Аванс": false,
        "Исп. Трансфер": false,
        "Исп. Итого": false,
        "Исп. Примечание": false
      },
      isIncludeTW: false,
      twKeys: [
        "Исп. Имя", "Исп. Телефон", "Исп. ИНН", "Исп. Ставка", "Исп. Дата начала", "Исп. Дата окончания",
        "Исп. Обед", "Исп. Количество", "Исп. Аванс", "Исп. Трансфер", "Исп. Итого", "Исп. Примечание",
      ],
      currentPayOrganization: null,
      currentPayTaskData: null,
      isFiltersOpen: false,
    }
  },
  async created() {
    this.isBusy = true;
    const query = this.$route.query;

    if (query) {
      if (query.query) {
        this.filters.query = query.query;
      }

      if (query.start_date) {
        this.filters.start_date = query.start_date;
      } else {
        this.filters.start_date = this.getStartDateFilter();
      }

      if (query.end_date) {
        this.filters.end_date = query.end_date;
      }

      if (query.status_id) {
        this.filters.status_id = query.status_id;
      }

      if (query.paid_workers) {
        this.filters.paid_workers = query.paid_workers;
      }

      if (query.locations) {
        const decodeLoc = decodeURIComponent(query.locations);
        this.filters.locations = decodeLoc.split(",");
      }

      if (query.users) {
        const decodeLoc = decodeURIComponent(query.users);
        this.filters.users = decodeLoc.split(",");
      }
    } else {
      this.filters.start_date = this.getStartDateFilter();
      this.filters.end_date = null;
    }

    if (this.$route.hash) {
      this.currentPage = this.getPage(this.$route.hash);
    }

    await this.getExecutors();

    this.statuses = (await this.getTaskStatuses()).data.items;

    await this.onSearch();
  },
  computed: {
    calendarEvents() {
      return this.items.map(req => {
        const colors = {
          primary: '#007bff',
          secondary: '#6c757d',
          success: '#28a745',
          danger: '#dc3545',
          warning: '#ffc107',
          info: '#17a2b8',
          dark: '#343a40'
        };

        let status = 0;
        if (req.workers_count === req.plan_workers_count) {
          status = 3;
        } else if (req.workers_count === 0) {
          status = 1;
        } else if (req.workers_count > 0 && req.workers_count < req.plan_workers_count) {
          status = 2;
        }
        const style = this.getCalendarStatusStyle(status);

        let title = `${req.workers_count}/${req.plan_workers_count} ${req.location_name}`
        if (req.plan_workers_count === 0) {
          title = `${req.workers_count} ${req.location_name}`
        }
        return {
          id: req.id,
          title: title,
          start: req.start_date,
          end: req.end_date,
          backgroundColor: colors[style] || '#007bff',
          borderColor: colors[style] || '#007bff',
          extendedProps: { ...req }, // сохраняем исходные данные на случай клика
          allDay: false,
        }
      })
    },
    calendarOptions() {
      return {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, multiMonthPlugin, bootstrapPlugin],
        themeSystem: 'bootstrap',
        initialView: 'dayGridMonth', // Вид по умолчанию (Месяц)
        locale: 'ru', // Локализация на русский язык
        firstDay: 1, // Понедельник — первый день недели
        buttonText: {
          prev: "<",
          next: ">",
          today: 'Сегодня',
          day: 'День',
          month: 'Месяц',
          week: 'Неделя',
          year: 'Год'
        },
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: 'multiMonthYear,dayGridMonth,timeGridWeek,timeGridDay' // кнопки переключения месяц/неделя/день
        },
        multiMonthMaxColumns: 3,
        events: this.calendarEvents, // передаем наш computed-массив
        eventClick: this.handleEventClick, // метод при клике на заявку в календаре
        editable: false,
        eventDrop: function(info) {
          alert(info.event.title + " was dropped on " + info.event.start);

          if (!confirm("Are you sure about this change?")) {
            info.revert();
          }
        }
      }
    },
    activeStatuses() {
      return this.statuses;
    },
    activePaidWorkersStatuses() {
      return this.paidStatuses;
    },
    totalSelectedPrice() {
      return this.priceFormat(
        this.selected
        .map(obj => obj.total_price)
        .reduce((acc, current) => acc + current, 0),true
      );
    },
    totalSelectedWorkerPrice() {
      return this.priceFormat(
        this.selected
        .map(obj => obj.total_worker_price)
        .reduce((acc, current) => acc + current, 0),true
      );
    },
    totalSelectedHours() {
      return this.selected
        .map(obj => obj.total_hours)
        .reduce((acc, current) => acc + current, 0)
    },
    totalSelectedWorkers() {
      return this.selected
        .map(obj => obj.workers_count)
        .reduce((acc, current) => acc + current, 0)
    },
    visibleExportColumns() {
      if (this.isIncludeTW) {
        return this.tasksExportColumns;
      }

      const filteredColumns = {};
      for (const key in this.tasksExportColumns) {
        if (!this.twKeys.includes(key)) {
          filteredColumns[key] = this.tasksExportColumns[key];
        }
      }
      return filteredColumns;
    },
    visibleFields() {
      return this.fields.filter(field => field.visible);
    },
    renderedFields() {
      return [
        this.serviceStartField,
        ...this.visibleFields,
        this.serviceEndField
      ];
    },
    activeFilterTags() {
      const tags = [];
      const defaults = this.getDefaultFilters();
      const start = this.filters.start_date;
      const end = this.filters.end_date;

      if (start || end) {
        let dateTag = '';

        if (start && end) {
          dateTag = `${this.formatTableDate(start)} - ${this.formatTableDate(end)}`;
        } else if (start) {
          dateTag = `с ${this.formatTableDate(start)}`;
        } else {
          dateTag = `по ${this.formatTableDate(end)}`;
        }
        tags.push({ key: 'date', label: dateTag });
      }

      if (this.filters.locations.length > 0) {
        tags.push({ key: 'location', label: `Объекты: ${this.filters.locations.length}`});
      }

      if (this.filters.users.length > 0) {
        tags.push({ key: 'user', label: `Сотрудники: ${this.filters.users.length}`,});
      }

      if (this.filters.status_id !== defaults.status_id) {
        const foundItem = this.activeStatuses.find(s => s.id === this.filters.status_id);
        tags.push({ key: 'status', label: foundItem ? foundItem.name : 'Статус заявки' });
      }

      if (this.filters.paid_workers !== defaults.paid_workers) {
        const foundItem = this.paidStatuses.find(s => s.id === this.filters.paid_workers);
        tags.push({ key: 'paid', label: foundItem ? foundItem.name : 'Статус оплаты' });
      }

      return tags;
    },
  },
  async mounted() {
    if (this.$refs.location_multi_select) {
      await this.$refs.location_multi_select.configure(this.filters.locations);
    }

    if (this.$refs.user_multi_select) {
      await this.$refs.user_multi_select.configure(this.filters.users);
    }

    const tableEl = this.$refs.tasks.$el
    const offsetLeft = tableEl.getBoundingClientRect().left
    const stickColEl = document.querySelectorAll('.sticky-column')
    stickColEl.forEach((el) => {
      const { x } = el.getBoundingClientRect()
      el.style.left = `${x - offsetLeft}px`
    })
  },
  methods: {
    handleEventClick(info) {
      // Тут можно открывать b-modal с подробностями заявки
      //alert(`Кликнули на заявку: ${info.event.title}\nID заявки: ${info.event.extendedProps.id}`);

      this.onRowClicked(info.event.extendedProps);
    },
    async getExecutors() {
      const result = await this.getUsers();

      if (!result || !result.data) {
        this.executors = [];
        return;
      }

      this.executors = result.data.users;
    },
    visibleAdmins() {
      return this.hasPermission([PERM.TASK_EDIT_OTHER]);
    },
    async getItems() {
      const locationIds = this.filters.locations.map(function (item) {
        return "&locations[]=" + item;
      });

      const usersIds = this.filters.users.map(function (item) {
        return "&executors[]=" + item;
      });

      const data = {
        page: this.currentPage,
        size: this.perPage,
        query: this.filters.query,
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
        status_id: this.filters.status_id,
        paid_workers: this.filters.paid_workers,
        locations: locationIds.join(''),
        users: usersIds.join(''),
        sortBy: [this.sortBy],
        sortDesc: [this.sortDesc],
        include_tw: false,
      }

      this.isBusy = true;
      const result = await this.getTasks(data);

      if (!result || !result.data) {
        this.items = [];
        this.totalRows = 0;
        this.isBusy = false;
        this.showError("Что-то пошло не так. Попробуйте обновить страницу и повторить действие.");
        return;
      }

      this.items = result.data.tasks;
      this.totalRows = result.data.total;
      this.updateSelected();
      this.isBusy = false;
    },
    onCreate() {
      this.$router.push({ name: 'TasksAdd'}).catch(()=>{});
    },
    onEdit(item) {
      this.$router.push({ name: 'TasksEdit', params: { task_id: item.id }}).catch(()=>{});
    },
    onRead(item) {
      this.$router.push({ name: 'TasksView', params: { task_id: item.id }}).catch(()=>{});
    },
    onCopy(item) {
      this.$router.push({ name: 'TasksCopy', params: { task_id: item.id }}).catch(()=>{});
    },
    onRemove(row) {
      if (row.item) {
        this.deleteRow = row.item;
      }

      const title = "Удаление заявки";
      const message = `${this.$t("message.tasks.modal.removeDesc")} № ${this.deleteRow.id}?`;
      this.showModalDelete(title, message, async () => {
        const result = await this.deleteTask(this.deleteRow);

        if (result.error) {
          if (result.status === 422) {
            const message = this.$t('message.common.errorMessage');
            this.showError(message);
          }
          this.showToast('Ошибка при удалении.', 'Ошибка', 'danger');
        } else {
          this.showToast('Заявка удалена.', 'Успех', 'success');
          await this.getItems();
        }
      });
    },
    len(arr) {
      return arr ? arr.length : 0;
    },
    paidWorkers(row) {
      return this.yesOrNoPayIcon(row.item.paid_workers);
    },
    paid(row) {
      return this.yesOrNoText(row.item.paid);
    },
    // Filters
    removeFilterTag(tag) {
      if (tag.key === 'date') {
        this.filters.start_date = null;
        this.filters.end_date = null;
      }
      else if (tag.key === 'location') {
        this.filters.locations = [];
        this.$refs.location_multi_select.onClearSelected();
      }
      else if (tag.key === 'user') {
        this.filters.users = [];
        this.$refs.user_multi_select.onClearSelected();
      }
      else if (tag.key === 'status') {
        this.filters.status_id = 0;
      }
      else if (tag.key === 'paid') {
        this.filters.paid_workers = 0;
      }
      this.onSearch();
    },
    async onClearFilters() {
      const filters = this.getDefaultFilters();
      if (JSON.stringify(this.filters) === JSON.stringify(filters)) {
        return;
      }
      this.filters = filters;
      if (this.$refs.location_multi_select) {
        await this.$refs.location_multi_select.configure(this.filters.locations);
      }

      if (this.$refs.user_multi_select) {
        await this.$refs.user_multi_select.configure(this.filters.users);
      }
      await this.onSearch();
      this.updateSelected();
    },
    getDefaultFilters() {
      let filters = {
        query: '',
        start_date: null,
        end_date: null,
        status_id: 0,
        users: [],
        locations: [],
        paid_workers: 0
      }

      filters.start_date = this.getStartDateFilter();
      filters.end_date = null;
      return filters;
    },
    getStartDateFilter() {
      // TODO: Продумать как менять даты для роли бухгалтера через permissions
      if (this.hasPermission([PERM.STAFF])) {
        return this.getStartDateFilterAccountant();
      }

      let date = this.getNewDateToServer();
      date.setDate(date.getDate() - 60);
      return this.formatDate(date);
    },
    getStartDateFilterAccountant() {
      let date = this.getNewDateToServer();
      date.setDate(date.getDate() - 30);
      return this.formatDate(date);
    },
    onSelectLocation(locations) {
      this.filters.locations = locations.map(function (item) { return item.id; });
    },
    onSelectUser(users) {
      this.filters.users = users.map(function (item) { return item.id; });
    },
    // permissions
    isVisibleReadOnlyForRow(item) {
      if (this.hasPermission([PERM.TASK_EDIT_OTHER])) {
        return true;
      }

      if (this.hasPermission([PERM.TASK_EDIT])) {
        if (item.read_only) {
          return false;
        }
        return true;
      }

      return false;
    },
    canEditTasks() {
      return this.hasPermission([PERM.TASK_EDIT]);
    },
    // Export
    toggleTW() {
      this.isIncludeTW = !this.isIncludeTW;
    },
    mapTaskForExport(item, selectedColumns) {
      const mappedItems = [];

      const configTasksExport = (task, tw = null) => ({
        "Номер заявки": task.id,
        "Заказчик": task.customer_name,
        "Адрес заявки": task.start_address,
        "Объект": task.location_name,
        "Дата начала": this.formatTableDate(task.start_date),
        "Дата окончания": this.formatTableDate(task.end_date),
        "Итого исполнителям": task.total_worker_price,
        "Итого с заказчика": task.total_price,
        "Ставка вида работ": task.payment_first,
        "Менеджер": task.executor_name,
        "Автор": task.author_name,
        "Дата создания": this.formatTableDate(task.create_date),
        "Количество исполнителей": task.workers_count,
        "Добавленные расходы": task.additional_cost,
        "Заявленные расходы": task.declarated_cost,
        "Примечание": task.cargo_description,
        "Статус": task.status_name,
        "Исп. Имя": tw ? tw.worker_name : '',
        "Исп. Телефон": tw ? tw.worker_phone : '',
        "Исп. ИНН": tw && tw.worker ? tw.worker.inn : '',
        "Исп. Ставка": tw ? tw.price : '',
        "Исп. Дата начала": tw ? (tw.start_date ? this.formatTableDateTime(tw.start_date) : '') : '',
        "Исп. Дата окончания": tw ? (tw.end_date ? this.formatTableDateTime(tw.end_date) : '') : '',
        "Исп. Обед": tw ? tw.dinner : '',
        "Исп. Количество": tw ? tw.total_units : '',
        "Исп. Итого": tw ? tw.total_price : '',
        "Исп. Примечание": tw ? tw.description : '',
      });

      const createRow = (config) => {
        const excelData = {};
        for (const key in config) {
          if (selectedColumns[key]) {
            excelData[key] = config[key];
          }
        }
        return excelData;
      };

      if (item.task_workers && item.task_workers.length > 0) {
        item.task_workers.forEach(tw => {
          const config = configTasksExport(item, tw);
          mappedItems.push(createRow(config));
        });
      } else {
        const config = configTasksExport(item);
        mappedItems.push(createRow(config));
      }

      return mappedItems;
    },
    async provideExportData(selectedColumns) {
      const includeTW = Object.keys(selectedColumns)
        .filter(key => this.twKeys.includes(key))
        .some(key => selectedColumns[key] === true);

      const items = await this.getAllItems(includeTW);

      if (this.totalRows > 1000) {
        this.showSubmitError("Большое количество записей. Отфильтруйте данные, например, по периоду.");
        return [];
      }

      if (items.length === 0) {
        this.showSubmitError("Записей не найдено.");
        return [];
      }

      if  (!Object.values(this.tasksExportColumns).some(value => value === true)) {
        this.showSubmitError("Выберите столбцы для экспорта.");
        return;
      }

      return items;
    },
    async getAllItems(includeTW) {
      const locationIds = this.filters.locations.map(function (item) {
        return "&locations[]=" + item;
      });

      const usersIds = this.filters.users.map(function (item) {
        return "&executors[]=" + item;
      });

      const data = {
        page: 1,
        size: this.totalRows,
        query: this.filters.query,
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
        status_id: this.filters.status_id,
        paid_workers: this.filters.paid_workers,
        locations: locationIds.join(''),
        users: usersIds.join(''),
        sortBy: [this.sortBy],
        sortDesc: [this.sortDesc],
        include_tw: includeTW,
      }

      this.isBusyExport = true;
      const result = await this.getTasks(data);
      this.isBusyExport = false;

      if (result.data.tasks && result.data.tasks.length > 0) {
        return result.data.tasks;
      }

      return [];
    },
    readExcel() {
      this.$refs.importTasks.show(function () {
        const message = `Импорт заявок завершен`
        this.$bvToast.toast(message, {
          title: 'Импорт заявок',
          variant: 'success',
          autoHideDelay: 3000,
          solid: true
        });

        this.getItems();

      }.bind(this));
    },
    onRowClicked(item) {
      if (this.isVisibleReadOnlyForRow(item)) {
        this.onEdit(item);
      } else {
        this.onRead(item);
      }
    },
    async selectAllRows(checked) {
      if (checked) {
        if (this.totalRows > 10000) {
          this.showSubmitError("Большое количество записей. Отфильтруйте данные, например, по периоду.");
          this.allSelected = false;
          return;
        }

        this.selected = await this.getAllItems(false);
        this.updateSelected();
      } else {
        this.selected = [];
        this.updateSelected();
      }
    },
    rowClicked(item) {
      // Ищем уже выбранные
      const existItem = this.selected.findIndex(e => e.id === item.id);

      if (existItem !== -1) {
        this.selected.splice(existItem, 1);
        this.$set(item, 'selected', false);
      } else {
        this.selected.push(item);
        this.$set(item, 'selected', true);
      }
    },
    updateSelected() {
      this.items.forEach((item) => {
        const isSelected = this.selected.some(e => e.id === item.id);
        this.$set(item, 'selected', isSelected);
      });

      this.selected = this.selected.map(item => {
        const updatedItem = this.items.find(i => i.id === item.id);
        return updatedItem ? updatedItem : item;
      });
    },
    tbodyRowClass(item) {
      return item && item.selected ? ["b-table-row-selected", "table-primary", "pointer-cell"] : ["pointer-cell"];
    },
    // Статус
    getStatusName(status_id) {
      const status = this.statuses.find(e => e.id === status_id)
      return status != null ? status.name : 'Ошибка';
    },
    getStatusStyle(status_id) {
      switch (status_id) {
        case 1:
          return 'primary';
        case 2:
          return 'info';
        case 3:
          return 'secondary';
        case 4:
          return 'success';
        default:
          return '';
      }
    },
    getCalendarStatusStyle(status_id) {
      switch (status_id) {
        // Не заполнено
        case 1:
          return 'danger';
        // Заполнено частично
        case 2:
          return 'warning';
        // Заполнено
        case 3:
          return 'success';
        default:
          return 'dark';
      }
    },
    getWorkersCountText(item) {
      if (item.plan_workers_count === 0) {
        return `${item.workers_count}`;
      } else {
        return `${item.workers_count}/${item.plan_workers_count}`;
      }
    },
    onInvoice() {
      this.$router.push({ name: 'InvoiceAdd', params: { tasks: this.selected }}).catch(()=>{});
    },
    async onNewStatus() {
      const taskIds = this.selected.map(obj => obj.id);
      const data = {
        task_ids: taskIds,
        status_id: 1,
      }
      await this.postTaskUpdateStatus(data);
      await this.getItems();
    },
    async onPayStatus() {
      const message = "Изменение приведет к закрытию заявки для редактирования.\nВы уверены что хотите продолжить?";
      const result = await this.showAcceptChangeStatus(message);
      if (!result) { return; }

      const taskIds = this.selected.map(obj => obj.id);
      const data = {
        task_ids: taskIds,
        status_id: 2,
      }
      await this.postTaskUpdateStatus(data);
      await this.getItems();
    },
    async onPayWorkers() {
      const message = "Изменение приведет к оплате всем исполнителям в заявке.\nВы уверены что хотите продолжить?";
      const result = await this.showAcceptChangeStatus(message);

      if (!result) { return; }

      const taskIds = this.selected.map(obj => obj.id);
      this.currentPayTaskData = {
        task_ids: taskIds,
      };
      this.$refs.modalSelectPayOrganization.show();
    },
    async showAcceptChangeStatus(message) {
      const title = "Изменение статуса";
      return await this.showAcceptModal(title, message);
    },
    updateHeights() {
      let topOffset = 209;
      let offset = `${topOffset}px`;
      let style = { maxHeight: `calc(100vh - ${offset}) !important`, contentVisibility: 'auto'}
      if (this.selected.length > 0) {
        topOffset = 232;
        offset = `${topOffset}px`;
        style = { maxHeight: `calc(100vh - ${offset}) !important`, contentVisibility: 'auto'}
      }
      this.tableStyle = style;
    },
    showInvoice(item) {
      const routeData = this.$router.resolve({name: 'InvoiceEdit', params: { invoice_id: item.invoice_id }});
      window.open(routeData.href, '_blank');
    },
    onInvoiceFile(item) {
      const id = item.invoice_id;
      this.exportInvoiceFile(id, "pdf", 1);
    },
    async exportInvoiceFile(id, exportType, hasStamp) {
      const data = { id, exportType, hasStamp }

      const result = await this.getInvoiceFile(data);

      if (result.error || !result.data) {
        this.showGenerateFileError("Ошибка создания файла", "Не удалось выполнить создание файла. Попробуйте повторить позднее.");
        return;
      }

      const fileName = this.getFileName(result.headers.get('content-disposition')).replace(/[+]/g,' ');

      this.handleFileDownload(result.data, fileName);
    },
    getFileName(disposition) {
      const defaultName = "untitled";

      if (!disposition) return defaultName;

      try {
        const { attachment, filename } = parse(disposition);
        return attachment ? filename : defaultName;
      } catch (e) {
        console.error(e);
        return defaultName;
      }
    },
    async exportTimesheetFile() {
      if (this.totalRows > 10000) {
        const message = "Большое количество записей. Отфильтруйте данные например по периоду";
        this.showError(message);
        return;
      }

      if (!this.dateValidation(this.filters.start_date)) {
        const message = "Выберите дату начала в фильтре";
        this.showError(message);
        return;
      }

      if (!this.dateValidation(this.filters.end_date)) {
        const message = "Выберите дату окончания в фильтре";
        this.showError(message);
        return;
      }

      let taskIds = [];
      if (this.selected.length !== 0) {
        taskIds = this.selected.map(obj => obj.id);
      } else {
        const allTasks = await this.getAllItems(false);
        taskIds = allTasks.map(obj => obj.id);
      }

      const locationIds = this.filters.locations.map(function (item) {
        return "&locations[]=" + item;
      });

      const usersIds = this.filters.users.map(function (item) {
        return "&executors[]=" + item;
      });

      const data = {
        status_id: this.filters.status_id,
        paid_workers: this.filters.paid_workers,
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
        locations: locationIds.join(''),
        users: usersIds.join(''),
        task_ids: taskIds
      }

      this.isBusyExport = true;
      const result = await this.postTaskTimesheetFile(data);

      if (!result || !result.data) {
        const title = "Ошибка создания файла";
        const message = "Не удалось выполнить создание файла. Попробуйте повторить позднее.";
        this.showGenerateFileError(title, message);
        this.isBusyExport = false;
        return;
      }

      this.isBusyExport = false;
      const fileName = this.getFileName(result.headers.get('content-disposition')).replace(/[+]/g,' ');

      this.handleFileDownload(result.data, fileName);
    },
    async exportWorkTimesheetFile() {
      if (this.totalRows > 10000) {
        const message = "Большое количество записей. Отфильтруйте данные например по периоду";
        this.showError(message);
        return;
      }

      if (!this.dateValidation(this.filters.start_date)) {
        const message = "Выберите дату начала в фильтре";
        this.showError(message);
        return;
      }

      if (!this.dateValidation(this.filters.end_date)) {
        const message = "Выберите дату окончания в фильтре";
        this.showError(message);
        return;
      }

      let taskIds = [];
      if (this.selected.length !== 0) {
        taskIds = this.selected.map(obj => obj.id);
      } else {
        const allTasks = await this.getAllItems(false);
        taskIds = allTasks.map(obj => obj.id);
      }

      const locationIds = this.filters.locations.map(function (item) {
        return "&locations[]=" + item;
      });

      const usersIds = this.filters.users.map(function (item) {
        return "&executors[]=" + item;
      });

      const data = {
        status_id: this.filters.status_id,
        paid_workers: this.filters.paid_workers,
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
        locations: locationIds.join(''),
        users: usersIds.join(''),
        task_ids: taskIds
      }

      this.isBusyExport = true;
      const result = await this.postTaskWorkTimesheetFile(data);

      if (!result || !result.data) {
        const title = "Ошибка создания файла";
        const message = "Не удалось выполнить создание файла. Попробуйте повторить позднее.";
        this.showGenerateFileError(title, message);
        this.isBusyExport = false;
        return;
      }

      this.isBusyExport = false;
      const fileName = this.getFileName(result.headers.get('content-disposition')).replace(/[+]/g,' ');

      this.handleFileDownload(result.data, fileName);
    },

    getUnit(unit_code) {
      const unit = this.getUnitByCode(unit_code);

      if (!unit) { return ""; }

      return unit.shortName || "";
    },
    onSelectOrg(org) {
      if (org === 0) {
        this.currentPayOrganization = null;
        return;
      }
      this.currentPayOrganization = org;
    },
    async onPay() {
      if (this.currentPayTaskData == null) {
        const message = "Не данных для оплаты";
        this.showError(message);
        return;
      }

      if (this.currentPayOrganization == null) {
        const message = "Не выбрана организация для оплаты";
        this.showError(message);
        return;
      }

      await this.postTaskUpdatePay(this.currentPayTaskData, this.currentPayOrganization);

      this.currentPayOrganization = null;
      this.currentPayTaskData = null;

      await this.getItems();
    },
    async onSelectTaskStatus(status) {
      if (status != null) {
        const taskIds = this.selected.map(obj => obj.id);
        const data = {
          task_ids: taskIds,
          status_id: status.id,
        }
        await this.postTaskUpdateStatus(data);
        await this.getItems();
      }
    }
  },
  watch: {
    selected(newValue, oldValue) {
      if (newValue.length === 0) {
        this.indeterminate = false;
        this.allSelected = false;
      } else if (newValue.length === this.totalRows) {
        this.indeterminate = false;
        this.allSelected = true;
      } else {
        this.indeterminate = true;
        this.allSelected = false;
      }

      this.updateHeights();
    },
    totalRows(newValue, oldValue) {
      if (newValue === 0) {
        this.allSelectDisabled = true;
      } else if (newValue > 10000) {
        this.allSelectDisabled = true;
      } else {
        this.allSelectDisabled = false;
      }
    },
  },
}
</script>

<style>
#tasks td {
  vertical-align: middle;
}

.tasksSelect {
  min-width: 50px;
  text-align: center;
}

.tasksId {
  min-width: 50px;
  text-align: center;
}

.tasksLocation {
  min-width: 200px;
  max-width: 250px;
}

.tasksDate {
  min-width: 130px;
}

.tasksWorkers {
  min-width: 100px;
}

.tasksTotalHours {
  min-width: 100px;
}

.tasksTotalPrice {
  min-width: 120px;
}

.tasksTotalWorkerPrice {
  min-width: 150px;
}

.tasksStatus {
  min-width: 220px;
}

.tasksInvoice {
  min-width: 70px;
  text-align: center;
}

.tasksExecutor {
  min-width: 200px;
  max-width: 200px;
  white-space: nowrap;
  overflow-x: hidden;
  text-overflow: ellipsis;
}

.tasksAction {
  min-width: 80px;
  padding-right: 10px !important;
  text-align: center;
}

.export-button {
  margin-top: 4px;
}

/* Стили для плавной интеграции с Bootstrap-Vue */
.fc {
  font-family: inherit;
}
.fc-button-primary {
  background-color: #007bff !important;
  border-color: #007bff !important;
}
.fc-button-primary:hover {
  background-color: #0069d9 !important;
  border-color: #0062cc !important;
}
.fc-button-active {
  background-color: #0062cc !important;
  border-color: #005cbf !important;
}

/* 1. Уменьшаем кнопки календаря до размера Bootstrap SM */
.fc .fc-toolbar .btn {
  padding: 0.25rem 0.5rem !important;
  font-size: 0.875rem !important;
  line-height: 1.5 !important;
  border-radius: 0.2rem !important;
}

/* 2. Делаем стрелочки навигации (текстовые) жирными и аккуратными */
.fc .fc-toolbar .btn .fc-icon {
  font-size: 1rem !important;
  vertical-align: middle !important;
}

/* 3. Уменьшаем заголовок календаря (Месяц Год), чтобы он соответствовал кнопкам sm */
.fc .fc-toolbar-title {
  font-size: 1.25rem !important;
  font-weight: 600 !important;
}

.fc-event {
  cursor: pointer !important;
}
</style>



// WEBPACK FOOTER //
// src/components/Tasks/Tasks.vue